import { useRecoilValue } from 'recoil';
import { currentDatabaseSelector, selectedDatabaseIdState } from '@transport-insights/uikit';
import { useTemporaryTrafficManagementCostPercentTrend } from '@src/modules/performance/context/temporary-traffic-management-api-hooks';
import { Text } from '@chakra-ui/react';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: null,
  content: [
    {
      title: 'TTM cost as a percentage of NLTP',
      description:
  <Text fontSize="inherit" mb={2}>
    The percentage of year-to-date NLTP expenditure that is TTM expenditure for the RCA, at the end of each quarter.
    Also shows the average percentage at the end of each quarter for the peer group, and the 25th to 75th percentile range at the end of each quarter for all RCAs.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    All results provided by the RCAs.
  </Text>,
    },
  ],
};

function TemporaryTrafficManagementCostPercentage() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Cost Percentage Trend data
  const { data: costPercentTrend, isLoading: isLoadingCostPercentTrend } = useTemporaryTrafficManagementCostPercentTrend();
  const costPercentTrendQuarters = [
    ...new Set(costPercentTrend?.map((item) => `Q${item.quarter} ${item.lockYear}`)),
  ].sort();
  const costPercentTrendChartData = [
    {
      name: currentDatabase?.name,
      type: 'line',
      data: costPercentTrend?.map((item, index) => ({ x: costPercentTrendQuarters[index], y: Math.round(item.costPercent) })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Peer Group',
      type: 'line',
      data: costPercentTrend?.map((item, index) => ({ x: costPercentTrendQuarters[index], y: Math.round(item.peerGroupCostPercent) })),
      color: CHART_COLOURS.black,
    },
    {
      name: '25-75th Percentile',
      type: 'rangeArea',
      data: costPercentTrend?.map((item, index) => ({ x: costPercentTrendQuarters[index], y: [Math.round(item.national25thPercentile), Math.round(item.national75thPercentile)] })),
      color: CHART_COLOURS.lightgrey,
    },
  ];

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const costPercentTrendChartSeries = filterChartDataByName(
    costPercentTrendChartData,
    chartFilters,
  );

  return (
    <ReportSection
      title="TTM cost as a percentage of NLTP"
      informationSource={informationSource}
      isLoading={isLoadingCostPercentTrend}
      lockYear={costPercentTrend?.at(-1)?.lockYear}
      quarter={costPercentTrend?.at(-1)?.quarter}
      quarterFormat="ytd"
    >
      <LineRangeAreaChart
        title=""
        categories={costPercentTrendQuarters}
        series={costPercentTrendChartSeries}
        width="100%"
        height="280"
        prefix=""
        suffix="%"
        markers={markers}
        stroke={stroke}
        decimalPoints={0}
        yAxisLabel="NLTP Expenditure (%)"
        min={0}
      />
    </ReportSection>
  );
}

export default TemporaryTrafficManagementCostPercentage;
